<template>
  <div class="min-h-[calc(100vh-4rem)] bg-neutral-100 flex items-center">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      <div class="flex flex-col md:flex-row items-center">
        <div class="md:w-[58%] mb-10 md:mb-0 md:pl-6">
          <div class="mb-6">
            <h1 class="text-2xl md:text-3xl xl:text-4xl font-bold leading-tight">
              <span class="text-primary">小义AI</span>教育工具平台
            </h1>
            <p
              class="text-base md:text-lg xl:text-xl text-neutral-500 mt-2.5 whitespace-nowrap"
            >
              发现与分享<span class="text-primary">AI生成</span>的创意工具
            </p>
          </div>
          <p class="text-lg text-neutral-500 mb-8 max-w-lg">
            汇聚开发者使用AI技术创建的各类实用小工具，从图片处理到文本生成，应有尽有。
          </p>
          <div class="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <router-link
              to="/square"
              class="px-6 py-3 rounded-md bg-primary text-white font-medium hover:bg-primary/90 apple-transition shadow-sm text-center"
            >
              探索工具
            </router-link>
            <div
              @click="checkLogin('/submit')"
              class="cursor-pointer px-6 py-3 rounded-md bg-white text-primary border border-primary font-medium hover:bg-neutral-50 apple-transition text-center"
            >
              发布工具
            </div>
            <div
              @click="checkLogin('/user')"
              class="cursor-pointer px-6 py-3 rounded-md bg-white text-neutral-700 border border-neutral-300 font-medium hover:bg-neutral-50 apple-transition text-center"
            >
              <i class="fa fa-th-large mr-1"></i>我的应用
            </div>
          </div>

          <!-- 小义问宝 AI 搜索框 -->
          <div class="mt-8 max-w-xl">
            <div
              class="flex items-center bg-white rounded-xl border border-neutral-300 px-4 h-14 shadow-sm"
            >
              <span class="ask-badge">义</span>
              <input
                v-model="askKeyword"
                @keyup.enter="openAskBot"
                maxlength="100"
                placeholder="问问小义问宝：有没有批改作文的 AI 工具…"
                class="flex-1 ml-3 text-[15px] outline-none bg-transparent"
              />
              <button class="ask-go" @click="openAskBot">
                <i class="fa fa-search"></i>
              </button>
            </div>
            <div class="mt-2 text-xs text-neutral-400">
              支持自然语言提问 · 基于站内工具、提示词、需求内容作答
            </div>
          </div>

          <!-- 进入应用广场 -->
          <div class="mt-8">
            <router-link
              to="/square"
              class="inline-flex items-center text-sm text-neutral-500 hover:text-primary transition-colors"
            >
              进入应用广场，浏览全部工具、智能体、案例与需求
              <i class="fa fa-angle-right ml-1"></i>
            </router-link>
          </div>
        </div>

        <div class="md:w-[42%]">
          <div
            class="relative"
            @mouseenter="stopBannerTimer"
            @mouseleave="startBannerTimer"
          >
            <template v-if="bannerList.length > 0">
              <transition name="banner-fade" mode="out-in">
                <a
                  v-if="currentBanner && currentBanner.link"
                  key="link"
                  :href="currentBanner.link"
                  target="_blank"
                  class="block"
                >
                  <img
                    :src="currentBanner.image"
                    :alt="currentBanner.title || '轮播图'"
                    class="w-full h-auto rounded-xl shadow-lg hover-lift"
                  />
                </a>
                <img
                  v-else-if="currentBanner"
                  key="img"
                  :src="currentBanner.image"
                  :alt="currentBanner.title || '轮播图'"
                  class="w-full h-auto rounded-xl shadow-lg hover-lift"
                />
              </transition>
              <button
                v-if="bannerList.length > 1"
                @click="prevBanner"
                class="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 text-neutral-700 hover:bg-white items-center justify-center shadow-sm z-10"
                title="上一张"
              >
                <i class="fa fa-angle-left text-2xl"></i>
              </button>
              <button
                v-if="bannerList.length > 1"
                @click="nextBanner"
                class="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 text-neutral-700 hover:bg-white items-center justify-center shadow-sm z-10"
                title="下一张"
              >
                <i class="fa fa-angle-right text-2xl"></i>
              </button>
              <div
                v-if="bannerList.length > 1"
                class="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10"
              >
                <button
                  v-for="(b, i) in bannerList"
                  :key="b.id"
                  @click="gotoBanner(i)"
                  :class="[
                    'w-2.5 h-2.5 rounded-full transition-all',
                    i === currentBannerIndex
                      ? 'bg-white w-6'
                      : 'bg-white/50 hover:bg-white/80',
                  ]"
                ></button>
              </div>
            </template>
            <img
              v-else
              src="https://picsum.photos/id/180/600/400"
              alt="AI工具展示"
              class="w-full h-auto rounded-xl shadow-lg hover-lift"
            />
            <div class="absolute top-4 left-4">
              <span class="px-3 py-1 bg-secondary text-white text-xs rounded-full"
                >热门工具</span
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { globalState } from "@/state";
import { requireLogin } from "@/utils/authGuard";
import { normalizeSiteUrl } from "@/utils/siteLink";

export default {
  name: "HomeLandingPage",
  data() {
    return {
      bannerList: [],
      currentBannerIndex: 0,
      bannerTimer: null,
      askKeyword: "",
    };
  },
  computed: {
    currentBanner() {
      return this.bannerList[this.currentBannerIndex] || null;
    },
    user() {
      return this.$store.state.user;
    },
    isLogin() {
      return this.user && this.user.uid;
    },
  },
  created() {
    this.getBanners();
    this.checkHomePref();
  },
  beforeUnmount() {
    this.stopBannerTimer();
  },
  methods: {
    getBanners() {
      this.$axios({ url: "banner/list" })
        .then((res) => {
          if (res.code === 200) {
            this.bannerList = res.data || [];
            if (this.bannerList.length > 1) {
              this.startBannerTimer();
            }
          }
        })
        .catch(() => {});
    },
    startBannerTimer() {
      this.stopBannerTimer();
      if (this.bannerList.length < 2) return;
      this.bannerTimer = setInterval(() => {
        this.currentBannerIndex =
          (this.currentBannerIndex + 1) % this.bannerList.length;
      }, 4000);
    },
    stopBannerTimer() {
      if (this.bannerTimer) {
        clearInterval(this.bannerTimer);
        this.bannerTimer = null;
      }
    },
    nextBanner() {
      this.currentBannerIndex =
        (this.currentBannerIndex + 1) % this.bannerList.length;
    },
    prevBanner() {
      this.currentBannerIndex =
        (this.currentBannerIndex - 1 + this.bannerList.length) %
        this.bannerList.length;
    },
    gotoBanner(i) {
      this.currentBannerIndex = i;
    },
    /** 需要登录的操作：未登录引导登录，已登录校验后跳转 */
    checkLogin(router) {
      if (!this.isLogin) {
        requireLogin(this, "该功能");
        return;
      }
      this.$axios({ url: "aitool/user/checklogin" }).then((res) => {
        if (res.code == 200) {
          this.$router.push(router);
        }
      });
    },
    openAskBot() {
      const q = (this.askKeyword || "").trim();
      globalState.openAskBot(q);
      this.askKeyword = "";
    },
    /** 个人首页：已登录且设置过直达内容时，打开站点直接进入（每会话一次） */
    async checkHomePref() {
      if (!localStorage.getItem("token")) return;
      if (sessionStorage.getItem("__homePrefJumped")) return;
      try {
        const res = await this.$axios({ url: "user/home/pref" });
        const pref = res.code === 200 && res.data ? res.data.pref : null;
        if (!pref || !pref.type) return;

        // 站外内容（网站集合）：直接打开原网址
        if (pref.type === "web" || pref.external) {
          const href = normalizeSiteUrl(pref.link);
          if (!href) return;
          sessionStorage.setItem("__homePrefJumped", "1");
          window.open(href, "_blank", "noopener");
          return;
        }

        // 站内内容：按 Web 路由规则拼地址（小程序存的 link 不能直接复用）
        const routes = {
          tool: "/tool/",
          agent: "/agent/",
          case: "/cases",
          prompt: "/prompts",
          software: "/software",
          request: "/requests",
        };
        const base = routes[pref.type];
        if (!base) return;
        const link = base.endsWith("/") ? base + pref.id : base;
        sessionStorage.setItem("__homePrefJumped", "1");
        this.$router.push(link);
      } catch (e) {
        /* 静默 */
      }
    },
  },
};
</script>
